/*
 *   File : systematic-plan-order.js
 *   Author : https://evoqins.com
 *   Description : SWP / STP order detail modal (Other Transactions)
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { Icon } from '../Icon';

import style from '../../Style/Component/tooltip.module.scss';

const MODAL_ID = "systematic-plan-order-modal";

const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") {
        return "-";
    }
    return value;
};

const firstPresent = (...values) => {
    for (const value of values) {
        if (value !== null && value !== undefined && value !== "" && value !== "-") {
            return value;
        }
    }
    return null;
};

const formatCurrency = (value) => {
    const text = displayValue(value);
    if (text === "-") return "-";
    return String(text).includes("₹") ? text : `₹${text}`;
};

const formatInitiatedDate = (orderDate) => {
    if (!orderDate) return "-";

    const dateMatch = orderDate.match(/^(.*?\d{4})\s+(\d{1,2}:\d{2}\s*(?:AM|PM))$/i);
    return dateMatch ? `${dateMatch[1]} | ${dateMatch[2]}` : orderDate;
};

const getStatusTextClass = (status) => {
    const normalized = status?.toLowerCase();

    if (normalized === "active" || normalized === "success" || normalized === "successful" || normalized === "confirmed") {
        return "e-text-green-crayola";
    }
    if (normalized === "failed" || normalized === "cancelled" || normalized === "canceled") {
        return "e-text-maximum-red";
    }
    if (normalized === "pending" || normalized === "under review" || normalized === "under_review") {
        return "e-text-marigold";
    }
    if (normalized === "submitted" || normalized === "in progress") {
        return "e-text-bleu-de-france";
    }
    if (normalized === "inactive") return "e-text-taupe-gray";
    return "e-text-raisin-black";
};

const DetailRow = ({ label, labelContent, value, valueClass, truncate = false, tooltipId, isLast = false, size = "sm" }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const displayText = displayValue(value);
    const showTooltip = truncate && displayText !== "-" && isTruncated;
    const isMd = size === "md";
    const fontClass = isMd ? "e-font-16" : "e-font-14";
    const resolvedValueClass = valueClass || (isMd ? "e-text-smoky-black" : "e-text-raisin-black");
    const rowClass = isMd || !isLast ? "border-bottom py-2" : "pt-2";

    useEffect(() => {
        const el = textRef.current;
        if (!el || !truncate || displayText === "-") {
            setIsTruncated(false);
            return;
        }

        const checkTruncation = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        };

        checkTruncation();
        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(el);

        return () => resizeObserver.disconnect();
    }, [displayText, truncate]);

    return (
        <div className={`d-flex justify-content-between align-items-start gap-3 ${rowClass}`}>
            <p className={`mb-0 e-text-dove-gray e-primary-font ${fontClass} e-font-weight-400 line-height-24px flex-shrink-0`}>
                {labelContent || label}
            </p>
            {truncate ?
                <div className={`d-flex justify-content-end flex-grow-1 e-primary-font ${fontClass} e-font-weight-600 line-height-24px ${resolvedValueClass}`}
                    style={{ minWidth: 0 }}>
                    <p ref={textRef}
                        className='mb-0 text-truncate'
                        style={{ maxWidth: '100%', width: 'fit-content' }}
                        data-tooltip-id={showTooltip ? tooltipId : undefined}
                        data-tooltip-content={showTooltip ? displayText : undefined}>
                        {displayText}
                    </p>
                    {showTooltip &&
                        <Tooltip id={tooltipId}
                            place="bottom-end"
                            offset={4}
                            positionStrategy="fixed"
                            className={style.e_custom_tooltip}
                            style={{
                                width: 'auto',
                                maxWidth: '320px',
                                padding: '0.5rem 0.75rem',
                            }} />
                    }
                </div>
                :
                <p className={`mb-0 text-end text-break flex-grow-1 e-primary-font ${fontClass} e-font-weight-600 line-height-24px ${resolvedValueClass}`}
                    style={{ minWidth: 0 }}>
                    {displayText}
                </p>
            }
        </div>
    );
};

const PlanCard = ({ title, rows, backgroundColor = "#F5F3FF", size = "sm" }) => (
    <div className={`${size === "md" ? "h-100 " : ""}border-radius-8 p-3`} style={{ backgroundColor }}>
        <p className={`mb-2 ${size === "md" ? "e-text-dravit-grey" : "e-text-dark-gray"} e-primary-font e-font-14 e-font-weight-600 line-height-20px`}>
            {title}
        </p>

        {rows.map((row, index) => (
            <DetailRow key={row.label}
                label={row.label}
                labelContent={row.labelContent}
                value={row.value}
                valueClass={row.valueClass}
                truncate={row.truncate}
                tooltipId={row.tooltipId}
                isLast={index === rows.length - 1}
                size={size} />
        ))}
    </div>
);

const SystematicPlanOrderModal = ({ data = {}, handleClose = () => { }, planType, type }) => {
    const historyPushedRef = useRef(false);
    const ignorePopStateRef = useRef(false);
    const resolvedType = planType || type || "SWP";
    const isStp = String(resolvedType).toUpperCase() === "STP" || String(data.type).toUpperCase() === "STP";
    const isFailed = data.order_status?.toLowerCase() === "failed"
        || data.fp_order_status?.toLowerCase() === "failed";

    const switchOut = data.switch_out || {};
    const switchIn = data.switch_in || {};

    const orderRows = [
        { label: "Scheme", value: data.scheme_name || data.from_scheme || data.switch_out_scheme_name, truncate: true, tooltipId: "swp-order-scheme" },
        { label: "Folio", value: data.folio_no },
        { label: "SWP amount", value: data.amount_text },
        { label: "NAV", value: formatCurrency(data.nav) },
        { label: "NAV date", value: data.nav_date },
        { label: "Withdrawn units", value: data.withdrawn_units },
        { label: "Exit load", value: data.exit_load_text || data.exit_load },
    ];

    const installmentRows = [
        { label: "Start date", value: data.plan_start_date },
        { label: "SWP day", value: data.plan_day },
        { label: "SWP tenure", value: data.plan_tenure },
        { label: "Upcoming withdrawal", value: data.upcoming_installment_date },
        { label: "Last withdrawal", value: data.last_installment_date },
        {
            label: "Bank name (Available in Folio)",
            labelContent: <>Bank name <span style={{ color: '#2E8EE6', fontSize: '12px' }}>(Available in Folio)</span></>,
            value: data.folio_bank_name,
            truncate: true,
            tooltipId: "swp-order-bank-name",
        },
        { label: "Executed SWPs", value: data.executed_installments },
        { label: "Remaining SWPs", value: data.remaining_installments },
        { label: "Rayi order status", value: data.order_status, valueClass: getStatusTextClass(data.order_status) },
    ];

    const fpRows = [
        {
            label: "FP Order ID",
            value: data.fp_order_id,
            truncate: true,
            tooltipId: "swp-order-fp-id",
        },
        {
            label: "FP order status",
            value: data.fp_order_status,
            valueClass: getStatusTextClass(data.fp_order_status),
        },
    ];

    const failureRows = [
        { label: "Failed date", value: data.failed_date },
        { label: "Failure reason", value: data.failure_reason, truncate: true, tooltipId: "swp-order-failure-reason" },
    ];

    const transferFromRows = [
        { label: "Scheme", value: firstPresent(switchOut.name, data.from_scheme, data.switch_out_scheme_name), truncate: true, tooltipId: "stp-from-scheme" },
        { label: "Folio", value: firstPresent(switchOut.folio, data.folio_no) },
        { label: "STP amount", value: firstPresent(switchOut.amount, data.amount_text) },
        { label: "NAV", value: formatCurrency(firstPresent(switchOut.nav, data.nav, data.redeemed_nav)) },
        { label: "NAV date", value: firstPresent(switchOut.date, data.nav_date) },
        { label: "Units for Transfer", value: firstPresent(switchOut.units, data.withdrawn_units, data.redeemed_units) },
        { label: "Exit Load", value: firstPresent(data.exit_load_text, data.exit_load) },
        { label: "FP Order ID", value: data.fp_order_id, truncate: true, tooltipId: "stp-from-fp-id" },
        { label: "Rayi order status", value: data.order_status, valueClass: getStatusTextClass(data.order_status) },
        { label: "FP order status", value: data.fp_order_status, valueClass: getStatusTextClass(data.fp_order_status) },
    ];

    const transferToRows = [
        { label: "Scheme", value: firstPresent(switchIn.name, data.to_scheme, data.switch_in_scheme_name), truncate: true, tooltipId: "stp-to-scheme" },
        { label: "Folio", value: firstPresent(switchIn.folio, data.folio_no) },
        { label: "STP amount", value: firstPresent(switchIn.amount, data.amount_text) },
        { label: "NAV", value: formatCurrency(firstPresent(switchIn.nav, data.purchase_nav, data.allocated_nav)) },
        { label: "NAV date", value: firstPresent(switchIn.date, data.allocated_date) },
        { label: "Units transferred", value: firstPresent(switchIn.units, data.allocated_units) },
        { label: "FP Order ID", value: data.fp_order_id, truncate: true, tooltipId: "stp-to-fp-id" },
        { label: "Settlement date", value: firstPresent(switchIn.allocated_at, data.allocated_date) },
        { label: "Rayi order status", value: data.order_status, valueClass: getStatusTextClass(data.order_status) },
        { label: "FP order status", value: data.fp_order_status, valueClass: getStatusTextClass(data.fp_order_status) },
    ];

    const _dismissModal = () => {
        document.getElementById(`${MODAL_ID}-close`)?.click();
        document.querySelector(".modal-backdrop")?.remove();
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "unset";
        handleClose();
    };

    const _handleCloseModal = () => {
        if (historyPushedRef.current) {
            ignorePopStateRef.current = true;
            historyPushedRef.current = false;
            _dismissModal();
            window.history.back();
            return;
        }
        _dismissModal();
    };

    useEffect(() => {
        const modalEl = document.getElementById(MODAL_ID);
        if (!modalEl) return;

        const _handleModalShown = () => {
            if (!historyPushedRef.current) {
                window.history.pushState({ systematicPlanOrderModal: true }, "");
                historyPushedRef.current = true;
            }
        };

        const _handlePopState = () => {
            if (ignorePopStateRef.current) {
                ignorePopStateRef.current = false;
                return;
            }

            if (historyPushedRef.current || modalEl.classList.contains("show")) {
                historyPushedRef.current = false;
                _dismissModal();
            }
        };

        modalEl.addEventListener("shown.bs.modal", _handleModalShown);
        window.addEventListener("popstate", _handlePopState);

        return () => {
            modalEl.removeEventListener("shown.bs.modal", _handleModalShown);
            window.removeEventListener("popstate", _handlePopState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='modal fade'
            id={MODAL_ID}
            data-bs-backdrop='static'
            data-bs-keyboard='false'
            tabIndex='-1'
            aria-labelledby='systematicPlanOrderModalLabel'
            aria-hidden='true'>
            <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable'
                style={isStp ? undefined : { minWidth: '80vw' }}>
                <span id={`${MODAL_ID}-close`}
                    className='d-none'
                    data-bs-dismiss='modal'>
                </span>

                <div className='modal-content border-0 border-radius-28 p-4'>
                    <div className='modal-header border-0 border-bottom p-0 pb-3 justify-content-between align-items-start'>
                        <div>
                            <h6 id='systematicPlanOrderModalLabel'
                                className='mb-2 e-text-raisin-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'>
                                {displayValue(data.name)}
                            </h6>
                            <p className='mb-0 e-text-davys-grey-light e-primary-font e-font-14 e-font-weight-400 line-height-20px'>
                                {displayValue(data.name)} ({displayValue(data.customer_display_id)}) | Initiated {formatInitiatedDate(data.order_date)}
                            </p>
                        </div>

                        <Icon icon='close'
                            size='1.125rem'
                            className='d-flex cursor-pointer'
                            onClick={_handleCloseModal} />
                    </div>

                    <div className={`modal-body px-0 ${isStp ? "py-3" : "pt-3 pb-2"}`} style={{ overflowX: "hidden" }}>
                        {isStp ?
                            <>
                                <div className='row row-cols-1 row-cols-lg-2 g-3 mx-0'>
                                    <div className='col'>
                                        <PlanCard title='Transfer From'
                                            size="md"
                                            rows={transferFromRows} />
                                    </div>
                                    <div className='col'>
                                        <PlanCard title='Transfer To'
                                            size="md"
                                            rows={transferToRows} />
                                    </div>
                                </div>

                                {isFailed &&
                                    <div className='p-2 mt-3'
                                        style={{
                                            backgroundColor: "#F8C0C1",
                                            border: "1px solid #772D2C",
                                            borderRadius: "8px",
                                            color: "#772D2C",
                                        }}>
                                        <p className='mb-1 e-primary-font e-font-12 e-font-weight-600 line-height-16px'>
                                            Failed reason
                                        </p>
                                        <p className='mb-0 e-primary-font e-font-12 e-font-weight-400 line-height-16px'>
                                            {displayValue(data.failure_reason)}
                                        </p>
                                    </div>
                                }
                            </>
                            :
                            <div className='row g-3 mx-0'>
                                <div className='col-12 col-lg-6 d-flex flex-column gap-3'>
                                    <PlanCard title='SWP Order Details' rows={orderRows} />
                                    <PlanCard title='SWP Instalment Details' rows={installmentRows} />
                                </div>
                                <div className='col-12 col-lg-6 d-flex flex-column gap-3'>
                                    <PlanCard title='FP Details' rows={fpRows} />
                                    <PlanCard title='Failure Details' rows={failureRows} />
                                </div>
                            </div>
                        }
                    </div>

                    {isStp &&
                        <div className='modal-footer border-0 p-0 justify-content-start'>
                            <p className='mb-0 e-text-dark-silver e-primary-font e-font-12 e-font-weight-400 line-height-16px'>
                                Last updated on {data.order_status_updated_on}
                            </p>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default SystematicPlanOrderModal;
